import { useEffect, useState } from 'react';
import Iframe from 'react-iframe';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@revelio/auth';
import {
  CenterLoading,
  IFrameMessageTypes,
  METHODOLOGY_THEME_PARAM,
  type MethodologyTheme,
  PagePaths,
  sendMessageToIFrame,
  useListenForIFrameMessages,
} from '@revelio/core';
import { useChartTheme } from '@revelio/replots';

import { environment } from '../../environments/environment';
import {
  APP_PAGE_TYPE,
  usePageVisitTracking,
} from '../../hooks/usePageVisitTracking';
import { useBrand } from '../../shared/branding/brand-provider';
import { useKibanaLogger } from '../../shared/hooks/kibana-logger/kibana-logger';
import { useUnleashFlags } from '../../shared/unleash-flags/use-unleash-flag';
import styles from './iframed-pages.module.css';

const TAXONOMY_CATALOG_IFRAME_ID = 'taxonomy-catalog-iframe';
const FORCE_UPDATE_VERSION = '1.2';
const TAXONOMY_CATALOG_ORIGIN =
  process.env.PUBLIC_LOCAL_TAXONOMY_CATALOG === 'true'
    ? 'http://localhost:4200'
    : 'https://taxonomy.reveliolabs.com';
// Only the embedded catalog may steer the dashboard route.
const TAXONOMY_CATALOG_MESSAGE_ORIGINS = [TAXONOMY_CATALOG_ORIGIN];

const TaxonomyCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [iframeUrl, setIframeUrl] = useState<string>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { dashMetaCsrfToken, ory, user } = useAuthStore();
  const { theme } = useChartTheme();
  const { kibanaLogger } = useKibanaLogger();
  const featureFlags = useUnleashFlags();
  const brand = useBrand();

  const resolvedTheme: MethodologyTheme = theme;

  usePageVisitTracking({
    page: 'Taxonomy Catalog',
    pageId: PagePaths.TAXONOMY_CATALOG,
    pageType: APP_PAGE_TYPE,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    // Extract the path suffix after '/taxonomy-catalog'
    const pathSuffix = location.pathname.replace(
      `/${PagePaths.TAXONOMY_CATALOG}`,
      ''
    );
    const iframeBaseUrl = `${TAXONOMY_CATALOG_ORIGIN}${pathSuffix}`;
    const iframeUrlWithParams = new URL(`${iframeBaseUrl}${location.search}`);
    iframeUrlWithParams.searchParams.set('v', FORCE_UPDATE_VERSION);
    // Seed the theme in the URL so the catalog applies it on first paint rather
    // than flashing until the postMessage below lands. Safe to read at mount:
    // index.html's anti-flash script applies `data-theme` before React runs. Live
    // toggles go via postMessage — rebuilding this URL would reload the iframe.
    iframeUrlWithParams.searchParams.set(
      METHODOLOGY_THEME_PARAM,
      resolvedTheme
    );
    setIframeUrl(iframeUrlWithParams.toString());
  }, []); // Should only run once on mount. Let the iframe update it's own urls

  useListenForIFrameMessages(
    IFrameMessageTypes.TaxonomyCatalogUrlPath,
    ({ data: pathName, type }) => {
      if (pathName && typeof pathName === 'string') {
        navigate(`/${PagePaths.TAXONOMY_CATALOG}${pathName}`);
      } else {
        console.error('useListenForIFrameMessages: invalid path', {
          data: pathName,
          type,
        });
        kibanaLogger?.error('useListenForIFrameMessages: invalid path', {
          username: user?.email ?? undefined,
        });
      }
    },
    { allowedOrigins: TAXONOMY_CATALOG_MESSAGE_ORIGINS }
  );

  // Send kibanna data to the iframe
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        dashMetaCsrfToken,
        ory,
        dashMetaRoot: environment.DASH_META_API,
      },
      type: IFrameMessageTypes.TaxonomyCatalogKibanna,
    });
  }, [dashMetaCsrfToken, iframeLoaded, ory]);

  // Send feature flags to the iframe
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        flags: featureFlags,
      },
      type: IFrameMessageTypes.TaxonomyCatalogFeatureFlags,
    });
  }, [iframeLoaded, featureFlags]);

  // Send client name to the iframe for brand resolution
  useEffect(() => {
    if (!iframeLoaded) {
      return;
    }

    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        clientName: brand.clientName,
      },
      type: IFrameMessageTypes.TaxonomyCatalogBrand,
    });
  }, [iframeLoaded, brand.clientName]);

  // Send the resolved colour mode to the iframe, so a live Appearance change
  // reaches the catalog without a reload.
  useEffect(() => {
    if (!iframeLoaded) {
      return;
    }

    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        theme: resolvedTheme,
      },
      type: IFrameMessageTypes.TaxonomyCatalogTheme,
    });
  }, [iframeLoaded, resolvedTheme]);

  return (
    <div className={styles.container}>
      <Iframe
        id={TAXONOMY_CATALOG_IFRAME_ID}
        key={FORCE_UPDATE_VERSION}
        url={iframeUrl ?? ''}
        width="100%"
        height="100%"
        position="relative"
        styles={{ border: 'none' }}
        onLoad={() => setIframeLoaded(true)}
      />
      {!iframeLoaded && <CenterLoading className={styles.loader} />}
    </div>
  );
};

export default TaxonomyCatalog;
